import React, { useState, useEffect, useRef } from 'react';
import { Send, AlertCircle } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const WALK_OPTIONS = ['Yes', 'Mostly', 'No'];
const PEACEFUL_OPTIONS = [1, 2, 3, 4, 5];
const VISITOR_ID_KEY = 'petechtor_visitor_id';
const WAITLIST_EMAIL_KEY = 'petechtor_waitlist_email';

export const getOrCreateVisitorId = () => {
  let id = localStorage.getItem(VISITOR_ID_KEY);
  if (!id) {
    id = (window.crypto?.randomUUID?.()) || `v-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(VISITOR_ID_KEY, id);
  }
  return id;
};

const getApiUrl = () => process.env.REACT_APP_BACKEND_URL;

export const RainbowRoomFeedback = ({ onSubmit }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: '', email: '', petName: '', walkGarden: '', peaceful: '', fixFirst: '' });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tourComplete, setTourComplete] = useState(false);
  const pollRef = useRef(null);

  // Locked until EITHER: (a) this email is already approved on the waitlist
  // (including Founding Guardians) - unlocks immediately, no tour needed, or
  // (b) the Rainbow Room reports (via /api/rainbow-room/tour-complete) that
  // this visitor reached 3 minutes in the room. The 3-minute lock only applies
  // to visitors who haven't signed up / aren't approved yet.
  useEffect(() => {
    const visitorId = getOrCreateVisitorId();
    const approvedEmail = localStorage.getItem(WAITLIST_EMAIL_KEY);
    const API_URL = getApiUrl();

    const checkStatus = async () => {
      try {
        if (approvedEmail) {
          const guardianRes = await fetch(`${API_URL}/api/guardian-status?email=${encodeURIComponent(approvedEmail)}`);
          const guardianData = await guardianRes.json();
          if (guardianData.approved) {
            setTourComplete(true);
            if (pollRef.current) clearInterval(pollRef.current);
            return;
          }
        }

        const res = await fetch(`${API_URL}/api/rainbow-room/tour-status?visitorId=${encodeURIComponent(visitorId)}`);
        const data = await res.json();
        if (data.completed) {
          setTourComplete(true);
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch (err) {
        // Network hiccup - next poll/focus check will retry
      }
    };

    checkStatus();
    pollRef.current = setInterval(checkStatus, 10000);
    window.addEventListener('focus', checkStatus);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      window.removeEventListener('focus', checkStatus);
    };
  }, []);

  // Some mobile browsers restore the page from a back-forward cache on
  // reload/navigation, keeping the old success message on screen instead
  // of a fresh form. Reset on that specific restore so the form is always
  // ready for a new submission after a page reset.
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setIsSuccess(false);
        setError('');
        setForm({ name: '', email: '', petName: '', walkGarden: '', peaceful: '', fixFirst: '' });
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  const walkLabels = {
    Yes: t('feedbackWalkYes'),
    Mostly: t('feedbackWalkMostly'),
    No: t('feedbackWalkNo')
  };

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim()) { setError(t('feedbackErrorName')); return; }
    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) { setError(t('feedbackErrorEmail')); return; }
    if (!form.walkGarden) { setError(t('feedbackErrorWalk')); return; }
    if (!form.peaceful) { setError(t('feedbackErrorPeaceful')); return; }
    if (!form.fixFirst.trim()) { setError(t('feedbackErrorFixFirst')); return; }

    setIsSubmitting(true);
    try {
      await onSubmit({
        name: form.name,
        email: form.email,
        petName: form.petName,
        walkGarden: form.walkGarden,
        peaceful: Number(form.peaceful),
        fixFirst: form.fixFirst
      });
      setIsSuccess(true);
      setForm({ name: '', email: '', petName: '', walkGarden: '', peaceful: '', fixFirst: '' });
    } catch (err) {
      setError(err.message || 'Failed to send feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="rainbow-room-feedback" className="mt-6 p-6 rounded-2xl bg-slate-900/50 border border-slate-700/50" data-testid="rainbow-room-feedback-card">
      <h3 className="text-lg font-bold text-white mb-4">{t('feedbackHeading')}</h3>

      {!tourComplete ? (
        <p className="text-sm font-bold text-white" data-testid="rainbow-feedback-locked-message">
          This section is locked until you complete your tour in the Rainbow Room.
        </p>
      ) : isSuccess ? (
        <p className="text-sm text-emerald-400 font-medium" data-testid="rainbow-feedback-success">
          {t('feedbackSuccess')}
        </p>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              type="text"
              placeholder={t('feedbackNamePlaceholder')}
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              data-testid="rainbow-feedback-name-input"
              className="px-4 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50"
              disabled={isSubmitting}
            />
            <input
              type="email"
              placeholder={t('feedbackEmailPlaceholder')}
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              data-testid="rainbow-feedback-email-input"
              className="px-4 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50"
              disabled={isSubmitting}
            />
          </div>

          <input
            type="text"
            placeholder={t('feedbackPetNamePlaceholder')}
            value={form.petName}
            onChange={(e) => update('petName', e.target.value)}
            data-testid="rainbow-feedback-petname-input"
            className="w-full px-4 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50"
            disabled={isSubmitting}
          />

          <div>
            <p className="text-xs text-slate-400 mb-2">{t('feedbackWalkQuestion')}</p>
            <div className="flex gap-2">
              {WALK_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => update('walkGarden', opt)}
                  data-testid={`rainbow-feedback-walk-${opt.toLowerCase()}`}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                    form.walkGarden === opt
                      ? 'bg-gradient-to-r from-sky-400 to-indigo-400 text-white border-transparent'
                      : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:border-slate-500'
                  }`}
                  disabled={isSubmitting}
                >
                  {walkLabels[opt]}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs text-slate-400 mb-1">{t('feedbackPeacefulQuestion')}</p>
            <p className="text-[11px] text-slate-500 mb-2" data-testid="rainbow-feedback-peaceful-helper">
              {t('feedbackPeacefulHelper')}
            </p>
            <div className="flex gap-2">
              {PEACEFUL_OPTIONS.map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => update('peaceful', num)}
                  data-testid={`rainbow-feedback-peaceful-${num}`}
                  className={`w-9 h-9 rounded-full text-sm font-semibold border transition-colors ${
                    form.peaceful === num
                      ? 'bg-gradient-to-r from-sky-400 to-indigo-400 text-white border-transparent'
                      : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:border-slate-500'
                  }`}
                  disabled={isSubmitting}
                >
                  {num}
                </button>
              ))}
            </div>
          </div>

          <textarea
            placeholder={t('feedbackFixFirstPlaceholder')}
            value={form.fixFirst}
            onChange={(e) => update('fixFirst', e.target.value)}
            data-testid="rainbow-feedback-fixfirst-input"
            rows={3}
            className="w-full px-4 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/50 resize-none"
            disabled={isSubmitting}
          />

          {error && (
            <div className="flex items-center gap-2 text-xs text-rose-400" data-testid="rainbow-feedback-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            data-testid="rainbow-feedback-submit-btn"
            className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-medium text-slate-900 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-4 h-4" />
            {isSubmitting ? t('feedbackSendingBtn') : t('feedbackSubmitBtn')}
          </button>
        </form>
      )}
    </div>
  );
};
